import { api } from "./api";

/**
 * Typed client for the {@code /api/v1/metadata-sync/*} endpoints exposed
 * by the metadata-sync module. All calls require an ADMIN role on the
 * caller — the backend returns 403 otherwise, which the UI surfaces as
 * "feature not available for your account".
 */

export type SyncStatusRow = {
  sourceName:      string;
  cronExpression:  string | null;
  presetLabel:     string | null;
  enabled:         boolean;
  lastSyncedAt:    string | null;
  lastRunStarted:  string | null;
  lastRunEnded:    string | null;
  rowsUpserted:    number;
  lastStatus:      string;            // idle | running | ok | failed
  lastError:       string | null;
  lagSeconds:      number | null;
};

export type SyncStatusResponse = {
  sources:   SyncStatusRow[];
  queriedAt: string;
};

export type SyncScheduleRow = {
  sourceName:     string;
  cronExpression: string;
  presetLabel:    string | null;
  enabled:        boolean;
  updatedBy:      string | null;
  updatedAt:      string;
};

export type SyncScheduleResponse = { schedules: SyncScheduleRow[] };

export type SyncRunRow = {
  runId:        number;
  sourceName:   string;
  triggeredBy:  string;
  startedAt:    string;
  endedAt:      string | null;
  durationMs:   number | null;
  rowsUpserted: number | null;
  status:       string;
  errorMessage: string | null;
};

export type SyncRunsResponse = { runs: SyncRunRow[] };

export type SyncTriggerResponse = {
  sourceName:   string;
  outcome:      "RUNNING" | "OK" | "FAILED" | "SKIPPED_ALREADY_RUNNING";
  runId:        number;
  rowsUpserted: number;
  newWatermark: string | null;
  errorMessage: string | null;
};

export type SchedulePutBody = {
  cronExpression?: string;
  presetLabel?:    string;
  enabled?:        boolean;
};

/**
 * Preset chip definitions — keep in one place so the backend
 * CronValidator's named presets and the UI chips never drift apart.
 */
export const CRON_PRESETS: Array<{ label: string; cron: string }> = [
  { label: "Every 15 min",           cron: "0 */15 * * * *" },
  { label: "Every 30 min",           cron: "0 */30 * * * *" },
  { label: "Hourly (at :15)",        cron: "0 15 */1 * * *" },
  { label: "Hourly (at :30)",        cron: "0 30 */1 * * *" },
  { label: "Every 3 hours",          cron: "0 0 */3 * * *" },
  { label: "Every 6 hours",          cron: "0 0 */6 * * *" },
  { label: "Daily (02:45)",          cron: "0 45 2 * * *" },
  { label: "Daily (03:00)",          cron: "0 0 3 * * *" },
  { label: "Weekly Sunday (03:00)",  cron: "0 0 3 * * SUN" },
];

export async function fetchSyncStatus(): Promise<SyncStatusResponse> {
  const { data } = await api.get<SyncStatusResponse>("/metadata-sync/status");
  return data;
}

export async function fetchSyncSchedule(): Promise<SyncScheduleResponse> {
  const { data } = await api.get<SyncScheduleResponse>("/metadata-sync/schedule");
  return data;
}

export async function fetchSyncRuns(source?: string, limit = 50): Promise<SyncRunsResponse> {
  const params = new URLSearchParams();
  if (source) params.set("source", source);
  params.set("limit", String(limit));
  const { data } = await api.get<SyncRunsResponse>(
    `/metadata-sync/runs?${params.toString()}`
  );
  return data;
}

export async function triggerSync(sourceName: string): Promise<SyncTriggerResponse> {
  const { data } = await api.post<SyncTriggerResponse>(
    `/metadata-sync/trigger/${encodeURIComponent(sourceName)}`
  );
  return data;
}

export async function updateSchedule(
  sourceName: string,
  body:       SchedulePutBody,
): Promise<SyncScheduleRow> {
  const { data } = await api.put<SyncScheduleRow>(
    `/metadata-sync/schedule/${encodeURIComponent(sourceName)}`,
    body,
  );
  return data;
}

/** Format a millisecond duration as "1.2s" / "45s" / "1m 12s" / "2h 3m". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
  return `${Math.floor(ms / 3_600_000)}h ${Math.round((ms % 3_600_000) / 60_000)}m`;
}

/** Format an ISO timestamp string as "5m ago" / "3h ago" / "Apr 23, 14:22". */
export function formatRelative(isoTs: string | null | undefined): string {
  if (!isoTs) return "—";
  const then = new Date(isoTs).getTime();
  const now = Date.now();
  const diff = now - then;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(isoTs).toLocaleString(undefined, {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}
